import { useState, useEffect } from 'react';
import { useAccount, useMsal } from '@azure/msal-react';
import { getAuthenticatedClient, fetchToken } from '../Utils/tokenHelper';
import {jwtDecode} from "jwt-decode";
import { NO_RESULTS_GROUPS_KEY } from '../Constants/cacheKeys';
import { enableEnabledPricers, enableFrontdoor } from '../Utils/features';

const BASE_URL = enableFrontdoor ? process.env.REACT_APP_CONFIG_API_BASE_URL_FRONTDOOR : process.env.REACT_APP_CONFIG_API_BASE_URL;

export default function useModulesApi() {
    const [loading, setLoading] = useState(true);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [modules, setModules] = useState([]);
    const { accounts, instance } = useMsal();
    const account = useAccount(accounts[0]);

    useEffect(async () => {
        if (account && instance) {
            await executeRequest();
        }
    }, [account, instance]);

    const executeRequest = async () => {
        setLoading(true);
        setSuccess(false);
        setError(null);
        setModules([]);

        try {
            const token = await fetchToken({ account, msalInstance: instance, url: `${BASE_URL}/configurations/search` });

            let decodedToken;
            let groups = [];

            if (enableEnabledPricers) {
                decodedToken = jwtDecode(token.replace('Bearer ', ''));
                groups = decodedToken.groups || [];

                // Retrieve cached groups with no results from session storage
                const cachedNoResultsGroups = JSON.parse(sessionStorage.getItem(NO_RESULTS_GROUPS_KEY)) || [];

                // Filter out the cached groups from the current groups list
                const filteredGroups = groups.filter(group => !cachedNoResultsGroups.includes(group));

                for (const groupId of filteredGroups) {
                    try {
                        const { data } = await getAuthenticatedClient({
                            url: `${BASE_URL}/configurations/search`,
                            method: 'POST',
                            account,
                            msalInstance: instance,
                            body: {
                                ruleType: 'EqualsUsersMRPPortalClient',
                                configurationType: 'MRPPortalModules',
                                ruleValue: groupId
                            }
                        });
                        if (data && data.value && data.value.modules) {
                            setModules(data.value.modules);
                            setSuccess(true);
                            return;
                        }
                    } catch (error) {
                        if (error.response && error.response.status === 404 &&
                             error.response.data &&
                             error.response.data.messages &&
                             error.response.data.messages.includes("No configuration found for rule")) {

                            cachedNoResultsGroups.push(groupId);
                            sessionStorage.setItem(NO_RESULTS_GROUPS_KEY, JSON.stringify(cachedNoResultsGroups));
                            // Continue the loop if the specific 404 error is encountered
                            continue;
                        } else {
                            throw error;
                        }
                    }
                }

                throw new Error('No valid response received from any group');
            } else {
                // If enableEnabledPricers is false, fetch modules without ruleValue and groups
                const { data } = await (await getAuthenticatedClient({
                    url: `${BASE_URL}/configurations/search`,
                    method: 'POST',
                    account,
                    msalInstance: instance,
                    body: {
                        ruleType: 'EqualsUsersMRPPortalClient',
                        configurationType: 'MRPPortalModules'
                    }
                }));
                
                setModules(data.value.modules);
            }
        } catch (e) {
            console.error(e);
            setError(e);
        } finally {
            setLoading(false);
        }
    };

    return {
        loading,
        success,
        error,
        modules,
        refresh: executeRequest
    };
}
